import { createFileRoute } from "@tanstack/react-router";
import LocalityLanding from "@/pages/LocalityLanding";
import { getBlogCoverImage } from "@/utils/blogImages";

export const Route = createFileRoute("/properties-in/$locality")({
  head: ({ params }) => {
    const rawLocality = params.locality || "";
    const localityName = rawLocality
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const title = `Properties in ${localityName}, Mumbai | MNRE Real Estate`;
    const description = `Browse verified ${localityName} properties for sale — premium apartments, penthouses, villas & commercial spaces. View pricing, amenities & expert advisory on MNRE.`;
    const canonicalUrl = `https://mnre.in/properties-in/${rawLocality}`;
    
    // Select topic-tailored authentic Mumbai cover
    const relativeCover = getBlogCoverImage({ title: localityName, slug: rawLocality });
    const coverUrl = relativeCover.startsWith("http") ? relativeCover : `https://mnre.in${relativeCover}`;

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: "MNRE - Mohnish Nagpal Real Estate",
      areaServed: {
        "@type": "Place",
        name: `${localityName}, Mumbai`,
      },
      url: canonicalUrl,
      image: coverUrl,
      telephone: "+917021011304",
      address: {
        "@type": "PostalAddress",
        addressLocality: localityName,
        addressRegion: "Mumbai, Maharashtra",
        addressCountry: "IN",
      },
    };

    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://mnre.in/" },
        { "@type": "ListItem", position: 2, name: "Properties", item: "https://mnre.in/properties" },
        { "@type": "ListItem", position: 3, name: localityName, item: canonicalUrl },
      ],
    };

    const faqJsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `What is the average price to buy a flat in ${localityName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `Property prices in ${localityName} depend heavily on the specific avenue, building age, and developer profile. Across active inventory, 2 BHK apartments typically range between ₹3.2 Crore and ₹5.5 Crore, while premium 3 BHK residences range from ₹6.0 Crore to ₹11.5 Crore+.`,
          },
        },
        {
          "@type": "Question",
          name: `Is ${localityName} a good choice for real estate investment in Mumbai?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `Yes. ${localityName} benefits from enduring tenant demand from senior corporate executives, healthcare professionals, and entrepreneurs. While gross residential rental yields average 2.5% to 3.2%, capital appreciation is bolstered by high-quality society redevelopments and connectivity via the Mumbai Coastal Road and Metro network.`,
          },
        },
        {
          "@type": "Question",
          name: `What additional costs must be budgeted when buying in ${localityName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `In Mumbai, buyers should budget roughly 8% to 11% above the base agreement value for mandatory transaction costs: 6% Maharashtra Stamp Duty (including 1% Metro Cess), ₹30,000 capped Registration fee, society share capital fees, and 5% GST if buying an under-construction unit (0% GST if the building has an Occupancy Certificate).`,
          },
        },
      ],
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:image", content: coverUrl },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: coverUrl },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([jsonLd, breadcrumbJsonLd, faqJsonLd]),
        },
      ],
    };
  },
  component: LocalityLanding,
});
